/**
 * Ring builder v2
 * ---------------
 * Configurator for the rebuilt ring products (ticket 06's three-way split).
 *
 * Two rules shape everything here:
 *
 * 1. Never enumerate variants. These products carry up to ~1,700 and both
 *    Liquid and /products/{handle}.js break down at that size. The UI is built
 *    from option values alone, and price is COMPUTED as
 *    base[metal class] + stone[shape][option value]. A variant is only needed
 *    at add-to-cart, and is resolved on demand.
 *
 * 2. Carat and quality are two controls but one option. Shopify allows three
 *    options per product, so "1.50 ct. E/VVS2 Lab Grown Diamond (IGI)" is a
 *    single Center Stone Selection value. The customer never sees that flat
 *    list; they pick carat and quality separately and we compose the value.
 */
(function () {
  'use strict';

  var root = document.querySelector('[data-ring-builder]');
  var stateEl = document.querySelector('[data-ring-builder-state]');
  if (!root || !stateEl || typeof Vue === 'undefined') return;

  var state;
  try {
    state = JSON.parse(stateEl.textContent);
  } catch (e) {
    console.error('[ring-builder] could not parse state', e);
    return;
  }

  // Shown whenever we will not put a variant in the cart. The backend sends
  // its own wording for the cases it refuses; this covers the rest, so the
  // customer sees one consistent message however the request failed.
  var CALL_US =
    'We need a moment to build this exact combination. Call or text ' +
    '(402) 650-2323 and we will finish it with you.';

  // "1.50 ct. E/VVS2 Lab Grown Diamond (IGI)" -> carat + quality
  var DIAMOND = /^([\d.]+)\s*ct\.\s*(\S+)\s/;
  // "1.50 ct. / 8x6mm Moissanite" -> size, which is its own dimension.
  // That figure is a Diamond Equivalent Weight. It read "1.50 DEW" until the
  // client relabelled it "ct." on 2026-08-28, with the DEW disclaimer moved
  // into the details section.
  var MOISSANITE = /^([\d.]+)\s*ct\.\s*\/\s*(\S+)\s/;

  function parseStone(value) {
    if (value.indexOf('Choose Your Own') === 0) {
      return { value: value, chooseOwn: true };
    }
    // Moissanite is tested FIRST, because since the relabel its values also
    // satisfy DIAMOND: that pattern would read the "/" as a clarity grade and
    // silently drop the size. The stricter pattern has to win.
    var m = MOISSANITE.exec(value);
    if (m) return { value: value, carat: m[1], dims: m[2], moissanite: true };
    m = DIAMOND.exec(value);
    if (m) return { value: value, carat: m[1], quality: m[2] };
    return { value: value };
  }

  // "1.50 ct. E/VVS2 Lab Grown Diamond (IGI)" -> IGI.
  var CERT = /\(([A-Za-z]+)\)\s*$/;
  function certOf(value) {
    var m = CERT.exec(value || '');
    return m ? m[1] : null;
  }

  /**
   * Tier names from the approved prototype (ticket 09).
   *
   * Keyed by the grade each name was shown against, not by position: a ring
   * whose option values happen to be ordered differently would otherwise be
   * given the wrong names, and a wrong name is worse than none. A grade with
   * no entry shows its grade alone.
   *
   * The prototype also showed a cut grade. That is not in the option value or
   * any metafield we read, so it is left out rather than invented.
   */
  var TIER_NAMES = {
    'lab-grown': { 'F/VS2': 'Classic', 'E/VVS2': 'Premium', 'D/VVS1': 'Mikado Signature' },
    natural: { 'H/SI2': 'Classic', 'F/VS2': 'Premium' },
    moissanite: { 'G/VVS2': 'Classic', 'D/VVS1': 'Premium' }
  };

  /** Cut grade per tier, as agreed with the client (ticket 02). */
  var TIER_CUTS = {
    'lab-grown': { 'F/VS2': 'Ideal', 'E/VVS2': 'Ideal', 'D/VVS1': 'Super Ideal' },
    natural: { 'H/SI2': 'Excellent', 'F/VS2': 'Excellent' },
    moissanite: { 'G/VVS2': 'Excellent', 'D/VVS1': 'Excellent' }
  };

  // Approved prototype's icon set and swatch colours (ticket 09). Radiant
  // reuses Emerald's outline, consistent with it inheriting Emerald's imagery
  // and pricing everywhere else on this project.
  var SHAPE_ICONS = {
    Round: '<circle cx="12" cy="12" r="9"/>',
    // A princess cut is a square with a chamfered table and corner facets,
    // not a plain square. The plain rect read as a placeholder.
    Princess: '<rect x="4" y="4" width="16" height="16"/><path d="M4 4l4.5 4.5M20 4l-4.5 4.5M4 20l4.5-4.5M20 20l-4.5-4.5"/><rect x="8.5" y="8.5" width="7" height="7"/>',
    Square: '<rect x="4" y="4" width="16" height="16"/>',
    Asscher: '<polygon points="7,4 17,4 20,7 20,17 17,20 7,20 4,17 4,7"/>',
    Heart: '<path d="M12 20 C4 13 2 9 5 6 C7.5 4 10.5 5 12 8 C13.5 5 16.5 4 19 6 C22 9 20 13 12 20 Z"/>',
    Oval: '<ellipse cx="12" cy="12" rx="7" ry="9.5"/>',
    Emerald: '<polygon points="7,4 17,4 20,8 20,16 17,20 7,20 4,16 4,8"/>',
    Radiant: '<polygon points="7,4 17,4 20,8 20,16 17,20 7,20 4,16 4,8"/>',
    Pear: '<path d="M12 3 C9 8 4 12 4 16 a8 8 0 0 0 16 0 c0 -4 -5 -8 -8 -13 Z"/>',
    Cushion: '<rect x="4" y="4" width="16" height="16" rx="6"/>',
    Marquise: '<path d="M12 3 C16 8 18 12 12 21 C6 12 8 8 12 3 Z"/>'
  };

  var METAL_SWATCH = {
    '14K White Gold': '#D9D9D6',
    '14K Yellow Gold': '#E7C275',
    '14K Rose Gold': '#E3B7A6',
    '18K White Gold': '#D9D9D6',
    '18K Yellow Gold': '#E7C275',
    '18K Rose Gold': '#E3B7A6',
    Platinum: '#C9CBCC'
  };

  // Photography is shot per colour group, not per metal: the three whites
  // share one shot. Used only to widen a media lookup that missed exactly.
  function colourGroup(metal) {
    var m = (metal || '').toLowerCase();
    if (m.indexOf('rose') !== -1) return 'rose';
    if (m.indexOf('yellow') !== -1) return 'yellow';
    return 'white';
  }

  /**
   * The key the video_link metafields are stored under.
   *
   * Confirmed live on a product that has video: the variant barcode reads
   * "WhiteRound" / "RoseRound", i.e. capitalised colour group + shape with no
   * separator. Ticket 09 recorded this as "White Gold-Round", which does not
   * exist anywhere in the catalogue.
   */
  function videoKey(metal, shape) {
    var group = colourGroup(metal);
    return group.charAt(0).toUpperCase() + group.slice(1) + (shape || '');
  }

  /**
   * Where "choose your own" sends the customer, per stone type.
   *
   * Moissanite is absent on purpose. The client was explicit: "There's no
   * reason to have a 'select your own stone' option for moissanite either."
   */
  /** The diamond a customer started with; see attachRememberedDiamond. */
  var DIAMOND_MEMO = 'ringBuilderDiamond';

  var DIAMOND_COLLECTIONS = {
    'lab-grown': 'loose-lab-grown-diamonds',
    natural: 'loose-natural-diamonds'
  };

  /**
   * Ring shape -> the diamond search widget's own shape value.
   *
   * Identity today, and deliberately kept as a table anyway. Ticket 05 needed
   * a real translation because the rings said "Square" while the widget said
   * "Princess", and because shapes like Round cover several cut names. Both
   * reasons have since gone: the client renamed Square to Princess on the new
   * products, and the widget expands its own groups internally
   * (SHAPE_GROUPS in mikado-search-vue3, applied as
   * `values: SHAPE_GROUPS[s.name] || [s.name]`), so passing "Round" already
   * matches "Round Modified Brilliant" too.
   *
   * The table stays so the next divergence is one line rather than a bug: the
   * two vocabularies are maintained by different people for different reasons.
   */
  /**
   * What a shape used to be called, for data written before the rename.
   *
   * The client renamed Square to Princess for customers. Photo alt tags still
   * carry the old name; video metafields carry the new one.
   */
  var LEGACY_SHAPE_NAMES = { Princess: 'Square' };

  /**
   * Shapes whose photos are filed under another shape.
   *
   * Radiant has no imagery of its own. Client 2026-08-08: "I don't have
   * radiant images so the images for the radiant variant will be tied to the
   * emerald images/videos." The seed does that for the variant image and the
   * video publisher writes a Radiant key copied from Emerald, so the main
   * photo and the video were right. This filter was the one place still
   * asking for a Radiant tag, and finding none it fell through to the
   * untagged photos: one white round shot (client, 2026-09-21).
   */
  var BORROWED_SHAPE_PHOTOS = { Radiant: 'Emerald' };

  /** Prefixes that mark a photo tag as belonging to one metal colour. */
  var COLOUR_GROUPS = ['White', 'Yellow', 'Rose'];

  /**
   * Every alt tag that could mean "this metal, this shape".
   *
   * Two formats are in use across the catalogue and neither is rare:
   *
   *   "White Gold-Pear"   the metal without its karat, then the shape   (~82%)
   *   "WhitePear"         the colour group and shape run together       (~18%)
   *
   * Plus both again under the old shape name, because the photos were tagged
   * before the client renamed Square to Princess. Trying all of them costs a
   * few selector lookups and avoids depending on which pass happened to tag a
   * given ring.
   */
  function mediaKeys(metal, shape) {
    var shapes = [shape];
    if (LEGACY_SHAPE_NAMES[shape]) shapes.push(LEGACY_SHAPE_NAMES[shape]);
    if (BORROWED_SHAPE_PHOTOS[shape]) shapes.push(BORROWED_SHAPE_PHOTOS[shape]);

    // "14K White Gold" -> "White Gold"; "Platinum" is already bare.
    var metalBase = String(metal || '').replace(/^\d+K\s+/, '');

    var out = [];
    shapes.forEach(function (sh) {
      out.push(videoKey(metal, sh));       // WhitePear
      out.push(metalBase + '-' + sh);      // White Gold-Pear
    });
    return out;
  }

  var SEARCH_SHAPES = {
    Round: 'Round',
    Princess: 'Princess',
    Cushion: 'Cushion',
    Oval: 'Oval',
    Emerald: 'Emerald',
    Asscher: 'Asscher',
    Pear: 'Pear',
    Heart: 'Heart',
    Marquise: 'Marquise',
    Radiant: 'Radiant'
  };

  var STONE_TYPE_LABELS = {
    'lab-grown': 'Lab Grown Diamond',
    natural: 'Natural Diamond',
    moissanite: 'Moissanite'
  };

  function formatMoney(amount, format) {
    var withCommas = Math.round(amount).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    if (format && format.indexOf('{{amount_no_decimals}}') !== -1) {
      return format.replace('{{amount_no_decimals}}', withCommas);
    }
    return '$' + withCommas;
  }

  var stones = state.options.stone.map(parseStone);

  Vue.createApp({
    delimiters: ['${', '}'],

    data: function () {
      var sel = state.selected;
      var current = parseStone(sel.stone || state.options.stone[0]);
      return {
        state: state,
        metal: sel.metal || state.options.metal[0],
        shape: sel.shape || state.options.shape[0],
        carat: current.carat || null,
        quality: current.quality || null,
        chooseOwn: !!current.chooseOwn,
        variantId: sel.variantId,
        // No default. A ring shipped in the wrong size is a remake, so the
        // customer has to choose one rather than inherit a guess.
        ringSize: null,
        // A real, cert-numbered diamond chosen through the search, attached to
        // this configuration. Null until the customer comes back with one.
        diamond: null,
        diamondLoading: false,
        // The "what do these grades mean" panel next to the tier table.
        showQualityInfo: false,
        resolving: false,
        adding: false,
        error: null
      };
    },

    computed: {
      metals: function () { return this.state.options.metal; },
      shapes: function () { return this.state.options.shape; },

      /**
       * Moissanite sizes are shape-specific, so the carat list depends on the
       * chosen shape. Diamonds offer the same carats across every shape.
       */
      availableStones: function () {
        var forShape = this.state.stonePrices[this.shape];
        if (!forShape) return stones.filter(function (s) { return !s.chooseOwn; });
        return stones.filter(function (s) {
          return s.chooseOwn || Object.prototype.hasOwnProperty.call(forShape, s.value);
        });
      },

      /**
       * Smallest to largest, whatever order the option values arrive in.
       *
       * Shopify keeps each option value once, so a moissanite size two shapes
       * share ("1.00 ct. / 6.0mm") only exists in the first shape's run of
       * values. Any later shape that also offers it picked it up out of
       * sequence: Radiant read 4.00 5.00 1.25 2.00 3.00 (client, 2026-09-21).
       */
      carats: function () {
        var seen = {};
        return this.availableStones
          .filter(function (s) { return s.carat && !seen[s.carat] && (seen[s.carat] = 1); })
          .map(function (s) { return s.carat; })
          .sort(function (a, b) { return Number(a) - Number(b); });
      },

      /** Qualities offered at the currently chosen carat. */
      qualities: function () {
        var carat = this.carat;
        var seen = {};
        return this.availableStones
          .filter(function (s) {
            return s.quality && s.carat === carat && !seen[s.quality] && (seen[s.quality] = 1);
          })
          .map(function (s) { return s.quality; });
      },

      hasQualityTiers: function () { return this.qualities.length > 0; },

      /**
       * The quality choices as priced cards, which is how the approved
       * prototype showed them and what the client asked for again. The point
       * is seeing what each grade costs without clicking through them one at
       * a time.
       *
       * Each card shows the TOTAL for that tier, not the stone's share of it.
       * The prototype showed a "+" figure, and the client asked for the whole
       * price instead: a customer comparing tiers is choosing between rings at
       * three prices, not pricing a stone separately, and an addition they
       * have to do themselves is one more step between them and the decision.
       */
      qualityTiers: function () {
        var self = this;
        var forShape = this.state.stonePrices[this.shape] || {};
        var names = TIER_NAMES[this.state.stoneType] || {};
        var cuts = TIER_CUTS[this.state.stoneType] || {};
        var base = this.basePrice;
        return this.qualities.map(function (q) {
          var stone = self.availableStones.filter(function (s) {
            return s.carat === self.carat && s.quality === q;
          })[0];
          var price = stone ? Number(forShape[stone.value] || 0) : 0;
          var total = base + price;
          return {
            quality: q,
            name: names[q] || '',
            grade: q.replace('/', ' / '),
            cut: cuts[q] || '',
            cert: stone ? certOf(stone.value) : null,
            price: price,
            total: total,
            // Quoted only when the base price is known. A ring with no base
            // price shows "Price on request" at the top, and a tier price
            // built from a missing base would contradict it.
            priceLabel:
              base > 0 && total > 0 ? formatMoney(total, self.state.moneyFormat) : ''
          };
        });
      },

      stoneTypeLabel: function () {
        return STONE_TYPE_LABELS[this.state.stoneType] || this.state.stoneType;
      },

      /**
       * The word the labels use for what is being chosen: "Diamond" or
       * "Stone".
       *
       * A moissanite is not a diamond, so calling its shape and weight
       * "Center Diamond" is wrong on the one product type where the
       * distinction is the whole point. Natural and lab grown are both
       * diamonds and read as such (client, 2026-09-16).
       */
      stoneWord: function () {
        return this.state.stoneType === 'moissanite' ? 'Stone' : 'Diamond';
      },

      /** The single Center Stone Selection value the controls compose to. */
      stoneValue: function () {
        if (this.chooseOwn) {
          var own = stones.filter(function (s) { return s.chooseOwn; })[0];
          return own ? own.value : null;
        }
        var carat = this.carat, quality = this.quality;
        var match = this.availableStones.filter(function (s) {
          if (s.carat !== carat) return false;
          return quality ? s.quality === quality : true;
        })[0];
        return match ? match.value : null;
      },

      /**
       * Keyed on the exact Material value rather than a karat class, even
       * though the client's pricing sheet is per class and the three colours
       * in a class now hold identical values. Keying on the metal costs
       * nothing and survives either model, which mattered when the catalogue
       * still priced colours apart and would matter again if it ever does.
       */
      basePrice: function () {
        return Number(this.state.basePrices[this.metal] || 0);
      },

      /**
       * Every seeded metal gets a base price metafield, so a miss means the
       * data is incomplete. Quote nothing rather than quote the stone alone.
       */
      priced: function () { return this.basePrice > 0; },

      stonePrice: function () {
        // Choose Your Own carries the bare setting price: the chosen diamond
        // is a separate line item (ticket 05).
        if (this.chooseOwn || !this.stoneValue) return 0;
        var forShape = this.state.stonePrices[this.shape] || {};
        return Number(forShape[this.stoneValue] || 0);
      },

      /**
       * What the customer pays for the configuration on screen. A chosen
       * diamond is still its own cart line (cartItems), but showing the
       * setting alone until the cart read as the whole ring's price (client,
       * 2026-10-29).
       */
      diamondPrice: function () {
        return this.chooseOwn && this.diamond ? Number(this.diamond.price || 0) : 0;
      },

      totalPrice: function () { return this.basePrice + this.stonePrice + this.diamondPrice; },

      priceFormatted: function () {
        return formatMoney(this.totalPrice, this.state.moneyFormat);
      },

      /** Stone types other than the one being viewed, for the switcher. */
      otherStoneTypes: function () {
        var siblings = this.state.siblings;
        if (!siblings) return [];
        var current = this.state.stoneType;
        var labels = {
          'lab-grown': 'Lab Grown Diamond',
          natural: 'Natural Diamond',
          moissanite: 'Moissanite'
        };
        return Object.keys(siblings)
          .filter(function (k) { return k !== current; })
          .map(function (k) {
            return { key: k, label: labels[k], handle: siblings[k] };
          });
      }
    },

    watch: {
      // Changing shape can invalidate the carat (moissanite) and changing
      // carat can invalidate the quality. Repair rather than render a
      // combination that does not exist.
      //
      // Then go through onSelectionChanged, the same path metal takes. It
      // used to call syncMedia alone, which swaps the featured image but does
      // not re-filter the gallery or update the URL: both of those hang off
      // syncUrl, which this never reached. On a ring where every shape offers
      // the same carat and quality, nothing else fired either, so choosing
      // Emerald left the Round photos on screen and the URL still saying
      // shape=Round.
      shape: function () { this.repairSelection(); this.onSelectionChanged(); },
      carat: function () { this.repairQuality(); },
      stoneValue: function () { this.onSelectionChanged(); },
      metal: function () { this.onSelectionChanged(); }
    },

    mounted: function () {
      this.hydrateFromUrl();
      this.onSelectionChanged();
      root.removeAttribute('data-loading');
    },

    methods: {
      swatchColour: function (metal) {
        // Unknown metals fall back to the line colour rather than to nothing,
        // so a new metal shows as a plain swatch instead of an invisible one.
        return METAL_SWATCH[metal] || '#e0e0e0';
      },

      /**
       * The store's own shape artwork, from snippets/icon-diamond-shapes.
       *
       * Falls back to the outline drawn in this file when a shape has no
       * artwork, so an option value the icon set does not cover still gets a
       * tile rather than an empty box. The fallback is a whole <svg> now,
       * because the markup no longer wraps this in one.
       */
      shapeIcon: function (shape) {
        var supplied = (this.state.shapeIcons || {})[shape];
        if (supplied) return supplied;
        var path = SHAPE_ICONS[shape] || '<circle cx="12" cy="12" r="9"/>';
        return (
          '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
          'stroke-width="1.3">' + path + '</svg>'
        );
      },

      // "14K White Gold" -> "14K White". The swatch is 40px wide and the full
      // name wraps to three lines; the colour itself carries the rest.
      shortMetal: function (metal) {
        return (metal || '').replace(' Gold', '');
      },

      /**
       * Show the photo for the current metal and shape by driving the theme's
       * own gallery rather than replacing it: every media is already rendered
       * as .product-media--featured and CSS hides all but data-active="true".
       *
       * Falls back to the same shape in the same colour group, because the
       * sparse seed does not create a variant for every pair and the three
       * whites share one photo anyway. If nothing matches, the current photo
       * stays put, which is better than blanking the gallery.
       */
      syncMedia: function () {
        var map = this.state.mediaByOption || {};
        var id = map[this.metal + '||' + this.shape];

        if (!id) {
          var group = colourGroup(this.metal);
          var shape = this.shape;
          Object.keys(map).some(function (k) {
            var parts = k.split('||');
            if (parts[1] === shape && colourGroup(parts[0]) === group) {
              id = map[k];
              return true;
            }
            return false;
          });
        }
        if (!id) return;

        var target = String(id);
        ['.product-media--featured', '.product-media--thumb-container',
         '.product-media--thumb'].forEach(function (selector) {
          var nodes = document.querySelectorAll(selector + '[data-id]');
          if (!nodes.length) return;
          for (var i = 0; i < nodes.length; i++) {
            nodes[i].setAttribute(
              'data-active', String(nodes[i].getAttribute('data-id') === target)
            );
          }
        });
      },

      repairSelection: function () {
        if (this.chooseOwn) return;
        if (this.carats.indexOf(this.carat) === -1) this.carat = this.carats[0] || null;
        this.repairQuality();
      },

      repairQuality: function () {
        if (this.chooseOwn) return;
        if (this.qualities.length && this.qualities.indexOf(this.quality) === -1) {
          this.quality = this.qualities[0];
        }
      },

      /**
       * A shared link may carry ?variant= (Shopify resolved it server-side, so
       * the page already painted correctly) or descriptive params, which is the
       * fallback for combinations that have no variant yet.
       */
      /** The hand-off is over once we are back on the ring. */
      clearReturnMemo: function () {
        try { sessionStorage.removeItem('ringBuilderReturnTo'); } catch (e) {}
      },

      hydrateFromUrl: function () {
        this.clearReturnMemo();
        var p = new URLSearchParams(window.location.search);
        if (p.get('metal')) this.metal = p.get('metal');
        if (p.get('shape')) this.shape = p.get('shape');
        if (p.get('carat')) this.carat = p.get('carat');
        if (p.get('quality')) this.quality = p.get('quality');
        var stone = p.get('stone');
        if (stone) {
          // "own" is the customer heading off to choose; anything else is the
          // handle of the diamond they came back with.
          this.chooseOwn = true;
          if (stone !== 'own') this.loadDiamond(stone);
        } else {
          this.attachRememberedDiamond();
        }
        this.repairSelection();
      },

      /**
       * A customer who started with a diamond and chose "Set it in a ring"
       * (snippets/diamond-to-ring.liquid) gets it on whichever ring they open.
       * A lab grown or natural diamond opens the matching version of the
       * ring, and the ring takes the diamond's shape. A ring not made in that
       * shape is left as it is rather than set with a stone that does not fit.
       */
      attachRememberedDiamond: function () {
        var memo = null;
        try { memo = JSON.parse(sessionStorage.getItem(DIAMOND_MEMO) || 'null'); } catch (e) {}
        if (!memo || !memo.handle) return;

        if (memo.type !== this.state.stoneType) {
          var sibling = (this.state.siblings || {})[memo.type];
          if (!sibling) return;
          var p = new URLSearchParams();
          p.set('metal', this.metal);
          if (memo.shape) p.set('shape', memo.shape);
          p.set('stone', memo.handle);
          window.location.replace(this.state.routes.products + sibling + '?' + p.toString());
          return;
        }
        if (!this.canChooseOwn()) return;
        if (memo.shape) {
          if (this.shapes.indexOf(memo.shape) === -1) return;
          this.shape = memo.shape;
        }
        this.chooseOwn = true;
        this.loadDiamond(memo.handle);
      },

      clearDiamondMemo: function () {
        try { sessionStorage.removeItem(DIAMOND_MEMO); } catch (e) {}
      },

      /**
       * replaceState rather than pushState: the back button should leave the
       * page, not step backwards through twenty option changes.
       */
      syncUrl: function () {
        var url = new URL(window.location.href);
        var p = url.searchParams;
        ['metal', 'shape', 'carat', 'quality', 'stone'].forEach(function (k) {
          p.delete(k);
        });
        if (this.variantId) {
          // Canonical: Shopify renders the right variant from this on reload.
          p.set('variant', this.variantId);
        } else {
          // No variant exists yet, so describe the configuration instead.
          p.delete('variant');
          p.set('metal', this.metal);
          p.set('shape', this.shape);
          if (this.chooseOwn) {
            p.set('stone', this.diamond ? this.diamond.handle : 'own');
          } else {
            if (this.carat) p.set('carat', this.carat);
            if (this.quality) p.set('quality', this.quality);
          }
        }
        window.history.replaceState({}, '', url.toString());
        this.announceConfig();
      },

      onSelectionChanged: function () {
        this.error = null;
        this.variantId = null;
        this.syncMedia();
        this.syncUrl();
        this.resolveVariant();
      },

      /**
       * Resolve the selected options to a variant id WITHOUT enumerating.
       * variantBySelectedOptions returns null when the combination has no
       * variant, which under the sparse seed is expected rather than an error.
       */
      resolveVariant: function () {
        var self = this;
        if (!this.stoneValue) return;
        var token = this.storefrontToken();
        if (!token) return;

        var query =
          'query($handle:String!,$opts:[SelectedOptionInput!]!){' +
          ' product(handle:$handle){ variantBySelectedOptions(selectedOptions:$opts){' +
          ' id availableForSale price{amount} } } }';

        this.resolving = true;
        fetch('/api/2026-01/graphql.json', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': token
          },
          body: JSON.stringify({
            query: query,
            variables: {
              handle: this.state.handle,
              opts: [
                { name: 'Material', value: this.metal },
                { name: 'Center Stone Shape', value: this.shape },
                { name: 'Center Stone Selection', value: this.stoneValue }
              ]
            }
          })
        })
          .then(function (r) { return r.json(); })
          .then(function (body) {
            var product = body && body.data && body.data.product;
            var variant = product && product.variantBySelectedOptions;
            if (variant && variant.id) {
              self.variantId = variant.id.split('/').pop();
              self.syncUrl();
            }
          })
          .catch(function (e) {
            // Not fatal: price is computed locally, and add-to-cart handles a
            // missing variant separately.
            console.warn('[ring-builder] variant lookup failed', e);
          })
          .finally(function () { self.resolving = false; });
      },

      storefrontToken: function () {
        var el = document.getElementById('shopify-features');
        if (!el) return null;
        try {
          return JSON.parse(el.textContent).accessToken || null;
        } catch (e) {
          return null;
        }
      },

      switchStoneType: function (handle) {
        // Carry shape and metal across, so switching stone type keeps as much
        // of the configuration as the sibling product can honour.
        var p = new URLSearchParams();
        p.set('metal', this.metal);
        p.set('shape', this.shape);
        window.location.href = this.state.routes.products + handle + '?' + p.toString();
      },

      /**
       * Tell the rest of the page which combination is showing.
       *
       * The 360 video lives outside this component and used to find its URL by
       * mapping variant id -> barcode through product.variants. Liquid
       * truncates that array at 250 and these products carry up to ~1,700
       * variants, so most selections resolved to nothing. Announcing the key
       * directly removes the variant hop entirely, and works for combinations
       * that have no variant yet.
       */
      /**
       * Show only the photos for the selected metal and shape.
       *
       * This is the theme's own mechanism, not a new one. Media carry their
       * alt text as data-setting-id, the old form matched that against the
       * variant's barcode, and both are the same "<ColourGroup><Shape>" key
       * this component already builds. The old form got this for free because
       * the theme drove variant selection; the builder drives its own, so it
       * has to do the same filtering.
       *
       * When the selection has photos of its own, ONLY those are shown, which
       * is what the legacy templates do: theme.js hides every tagged photo and
       * reveals the ones matching the variant's barcode. A ring with nothing
       * tagged for the selection keeps its whole gallery rather than going
       * blank.
       */
      filterMedia: function () {
        var tagged = [].slice.call(document.querySelectorAll('[data-setting-id]'));
        if (!tagged.length) return;

        // Photos were tagged before the client renamed Square to Princess, so
        // they read "WhiteSquare" while this component computes
        // "WhitePrincess". Try the current name, then the legacy one.
        var keys = mediaKeys(this.metal, this.shape);

        var mine = keys.reduce(function (acc, k) { return acc.concat(k); }, []);
        var tagOf = function (el) {
          return (el.getAttribute('data-setting-id') || '').trim();
        };
        var isMine = function (el) { return mine.indexOf(tagOf(el)) !== -1; };

        // Untagged photos used to be left visible, on the reasoning that they
        // were generic shots applying to any metal. They are not: they are
        // photos we have no colour for, and on a ring whose source was only
        // ever partly tagged they include the other metals. Arianna showed a
        // white gold selection alongside ten photos, several of them yellow
        // and rose.
        //
        // So once the selection has photos of its own, it gets those and
        // nothing else.
        var matching = tagged.filter(isMine);
        if (matching.length) {
          tagged.forEach(function (el) {
            el.style.display = isMine(el) ? 'block' : 'none';
          });
          this.showMainPhoto(matching);
          return;
        }

        // Nothing is tagged for this selection. Hiding on that basis would
        // leave an empty gallery, so fall back to hiding only what is clearly
        // another colour's and showing the rest.
        tagged.forEach(function (el) {
          var tag = tagOf(el);
          var belongsToAnother =
            tag &&
            (COLOUR_GROUPS.some(function (g) { return tag.indexOf(g) === 0; }) ||
              tag.indexOf('Platinum') === 0);
          el.style.display = belongsToAnother ? 'none' : 'block';
        });
      },

      /**
       * Put the selection's first photo in the main viewer.
       *
       * filterMedia only filters the thumbnails. The main photo is the
       * variant's, and theme.js changes it only when the theme selects a
       * variant, which the builder never does (and most combinations have no
       * variant yet). So an oval selection kept the round main photo, on every
       * shape page and on every shape picked by hand.
       *
       * Same attribute swap as theme.js updateVariantMedia, done here directly
       * because theme.js may not have bound its thumbnail handlers yet when
       * the builder first runs.
       */
      showMainPhoto: function (thumbContainers) {
        var activeThumb = document.querySelector('.product-media--thumb[data-active="true"]');
        var activeContainer = activeThumb && activeThumb.closest('[data-setting-id]');
        if (activeContainer && thumbContainers.indexOf(activeContainer) !== -1) return;

        var first = thumbContainers[0].querySelector('.product-media--thumb[data-id]');
        if (!first) return;
        var id = first.getAttribute('data-id');
        // The gallery theme.js drives; the mobile one is a separate slider.
        var featured = [].slice.call(document.querySelectorAll(
          '.product-media--root[data-view="featured"]:not([data-media-size="mobile"]) .product-media--featured[data-id]'
        ));
        if (!featured.some(function (el) { return el.getAttribute('data-id') === id; })) return;

        featured.forEach(function (el) {
          var on = el.getAttribute('data-id') === id;
          el.setAttribute('data-active', on ? 'true' : 'false');
          el.parentElement.style.display = on ? '' : 'none';
        });
        [].slice.call(document.querySelectorAll('.product-media--thumb[data-id]')).forEach(function (el) {
          el.setAttribute('data-active', el.getAttribute('data-id') === id ? 'true' : 'false');
        });
      },

      announceConfig: function () {
        this.filterMedia();
        window.dispatchEvent(
          new CustomEvent('ringbuilder:configchanged', {
            detail: {
              metal: this.metal,
              shape: this.shape,
              videoKey: videoKey(this.metal, this.shape)
            }
          })
        );
      },

      /**
       * Whether this ring can hand off to the diamond search at all.
       *
       * Lab Grown and Natural only. Moissanite has no choose-your-own path by
       * the client's decision, so the action is not offered there.
       */
      /** Format an amount the same way the ring price is formatted. */
      money: function (amount) {
        return formatMoney(amount, this.state.moneyFormat);
      },

      /**
       * Go straight to the diamond search.
       *
       * This used to only flip into the choose-your-own state, leaving the
       * customer looking at a second button to press. There is nothing to
       * decide in between, so the intermediate step is gone: the one button
       * that says "pick your own" takes them to the diamonds.
       */
      startChooseOwn: function () {
        this.chooseOwn = true;
        this.error = '';
        this.chooseOwnDiamond();
      },

      /** Back to the preset tiers, dropping any attached diamond. */
      cancelChooseOwn: function () {
        this.clearDiamondMemo();
        this.chooseOwn = false;
        this.diamond = null;
        this.error = '';
        this.onSelectionChanged();
      },

      canChooseOwn: function () {
        return !!DIAMOND_COLLECTIONS[this.state.stoneType];
      },

      /**
       * Send the customer to the diamond search, pre-filtered, with a way back.
       *
       * Same tab, not a new one: return_to brings them back to this exact
       * configuration. The search widget preserves params it does not
       * recognise, so return_to survives every filter interaction they make
       * while browsing without the widget needing to know about it.
       */
      chooseOwnDiamond: function () {
        var collection = DIAMOND_COLLECTIONS[this.state.stoneType];
        if (!collection) return;

        // Come back to the configuration as it stands, with the stone slot
        // marked as being chosen rather than already chosen.
        var back = new URL(window.location.href);
        back.searchParams.set('stone', 'own');

        var p = new URLSearchParams();
        var shape = SEARCH_SHAPES[this.shape];
        // Unmapped shape: send them unfiltered rather than to a filter value
        // the widget will not understand and silently replace with Round.
        if (shape) p.set('shape', shape);
        p.set('return_to', back.pathname + back.search);

        window.location.href = '/collections/' + collection + '?' + p.toString();
      },

      /**
       * Load the diamond the customer picked, by handle.
       *
       * Uses Shopify's product JSON rather than the Storefront API: no token,
       * no extra dependency, and it carries the variant id needed for the cart.
       */
      loadDiamond: function (handle) {
        var self = this;
        this.diamondLoading = true;
        fetch('/products/' + encodeURIComponent(handle) + '.js')
          .then(function (r) {
            if (!r.ok) throw new Error('diamond lookup failed');
            return r.json();
          })
          .then(function (product) {
            var variant = (product.variants || [])[0];
            if (!variant) throw new Error('diamond has no variant');
            self.diamond = {
              handle: product.handle,
              title: product.title,
              variantId: variant.id,
              // Shopify's product JSON is in cents; every other price in this
              // component is in whole currency units, from the metafields.
              price: variant.price / 100,
              available: variant.available
            };
            // The URL was written before the diamond loaded and still says
            // "own", so a refresh or a shared link dropped the diamond.
            self.syncUrl();
          })
          .catch(function () {
            // Never silently drop it: the price shown would then be the
            // setting alone while the customer believes a diamond is attached.
            self.diamond = null;
            self.error =
              'We could not load the diamond you picked. Please choose it again.';
          })
          .finally(function () { self.diamondLoading = false; });
      },

      removeDiamond: function () {
        this.clearDiamondMemo();
        this.diamond = null;
        this.error = '';
        this.syncUrl();
      },

      addToCart: function () {
        if (this.state.ringSizes.length && !this.ringSize) {
          this.error = 'Please choose a ring size.';
          return;
        }
        if (this.variantId) {
          this.postToCart(this.variantId);
          return;
        }
        this.createThenAdd();
      },

      /**
       * Ask the backend to create the variant for this combination.
       *
       * Under the sparse seed most combinations have no variant until someone
       * buys one (ticket 14). No price is sent: the backend recomputes it from
       * the same metafields and stone table this page rendered from, and
       * ignores a price supplied by the caller.
       *
       * On any failure the customer is routed to a human. We never fall back to
       * another variant, because on a ring at this price that silently charges
       * for a different stone.
       */
      createThenAdd: function () {
        var self = this;
        this.adding = true;
        this.error = '';

        fetch(this.state.routes.variantApi, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            handle: this.state.handle,
            metal: this.metal,
            shape: this.shape,
            stone: this.stoneValue
          })
        })
          .then(function (r) {
            return r
              .json()
              .catch(function () { return {}; })
              .then(function (body) {
                if (!r.ok || !body.variant_id) {
                  // The backend's refusals carry customer-safe wording, so
                  // prefer its message over our generic one.
                  throw new Error(body.message || CALL_US);
                }
                return String(body.variant_id);
              });
          })
          .then(function (variantId) {
            self.variantId = variantId;
            self.syncUrl();
            self.postToCart(variantId);
          })
          .catch(function (e) {
            self.adding = false;
            self.error = (e && e.message) || CALL_US;
          });
      },

      /**
       * The line items for this configuration.
       *
       * A chosen diamond is a SECOND line item, not folded into the ring's
       * price. It is a real cert-numbered product that has to be picked,
       * invoiced and shipped as itself. The two are linked by properties so
       * the cart and fulfilment can tell they belong together; the underscore
       * prefix keeps them out of the customer-facing cart display.
       */
      cartItems: function (variantId) {
        var ring = {
          id: Number(variantId),
          quantity: 1,
          // "Ring Size" is the key fulfilment reads: it is what
          // snippets/ring-size.liquid submits and what real orders carry. The
          // custom builder's "ring_size" is a separate, barely used path.
          properties: this.ringSize ? { 'Ring Size': this.ringSize } : {}
        };
        var items = [ring];
        if (this.diamond) {
          ring.properties._diamond = String(this.diamond.variantId);
          items.push({
            id: Number(this.diamond.variantId),
            quantity: 1,
            properties: { _ring: String(variantId) }
          });
        }

        // A finished ring, with a preset stone or a chosen diamond, comes
        // with the free insurance appraisal, as the old preset pages did
        // (client list item 8). A setting ordered without a stone does not.
        var complete = !this.chooseOwn || !!this.diamond;
        if (complete && this.state.appraisalVariantId) {
          items.push({
            id: Number(this.state.appraisalVariantId),
            quantity: 1,
            properties: { _ring: String(variantId) }
          });
        }
        return items;
      },

      postToCart: function (variantId) {
        var self = this;
        this.adding = true;
        fetch(this.state.routes.cartAdd, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: this.cartItems(variantId) })
        })
          .then(function (r) {
            if (!r.ok) throw new Error('cart/add failed');
            // The diamond is in the cart with this ring now; do not put it on
            // the next ring they open.
            self.clearDiamondMemo();
            window.location.href = '/cart';
          })
          .catch(function () {
            self.error = 'Sorry, we could not add that to your cart. Please try again.';
          })
          .finally(function () { self.adding = false; });
      }
    }
  }).mount('[data-ring-builder-mount]');
})();
